import React, { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Search,
  Filter,
  Plus,
  Download,
  Upload,
  TrendingUp,
  TrendingDown,
  DollarSign,
  CreditCard,
  Wallet,
  PiggyBank,
  FileText,
  Calendar,
  User,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Eye,
  Edit,
  Trash2,
  MoreVertical,
  RefreshCw,
  BarChart3,
  PieChart,
  ArrowUpRight,
  ArrowDownRight,
} from "lucide-react";
import { toast } from "react-hot-toast";
import api from "../../utils/axios.config";
import { baseUrl } from "../../utils/url";

const FinanceManagement = () => {
  const [activeTab, setActiveTab] = useState("overview"); // overview, payments, income, expense, reports
  const [viewMode, setViewMode] = useState("table"); // table, cards
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [modalType, setModalType] = useState(""); // payment, income, expense

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [monthFilter, setMonthFilter] = useState("");
  const [yearFilter, setYearFilter] = useState(
    new Date().getFullYear().toString()
  );
  const [currentPage, setCurrentPage] = useState(1);

  const queryClient = useQueryClient();

  // Get current user
  const getCurrentUser = () => {
    try {
      const userData = localStorage.getItem("user");
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      return null;
    }
  };

  // Fetch dashboard stats
  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ["financeStats"],
    queryFn: async () => await api.get("finance/dashboard/stats"),
    staleTime: 5 * 60 * 1000,
  });

  const stats = statsData?.data?.data || {};

  // Fetch transactions
  const {
    data: transactionsData,
    isLoading: transactionsLoading,
    refetch: refetchTransactions,
  } = useQuery({
    queryKey: [
      "transactions",
      {
        type: typeFilter,
        status: statusFilter,
        month: monthFilter,
        year: yearFilter,
        search: searchTerm,
        page: currentPage,
      },
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (typeFilter !== "all") params.append("type", typeFilter);
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (monthFilter) params.append("month", monthFilter);
      if (yearFilter) params.append("year", yearFilter);
      if (searchTerm) params.append("search", searchTerm);
      params.append("page", currentPage.toString());
      params.append("limit", "20");

      return await api.get(`finance/transactions?${params.toString()}`);
    },
    staleTime: 2 * 60 * 1000,
  });

  const transactions = transactionsData?.data?.data?.transactions || [];
  const pagination = transactionsData?.data?.data?.pagination || {};

  // Mutations
  const updateStatusMutation = useMutation({
    mutationFn: async ({ paymentId, status, adminNote }) => {
      return api.patch("finance/payment/status", {
        paymentId,
        status,
        adminNote,
      });
    },
    onSuccess: () => {
      toast.success("Payment status updated successfully");
      queryClient.invalidateQueries(["transactions"]);
      queryClient.invalidateQueries(["financeStats"]);
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message || "Failed to update payment status"
      );
    },
  });

  const addTransactionMutation = useMutation({
    mutationFn: async ({ type, data }) => {
      const endpoint =
        type === "payment"
          ? "finance/payment/add"
          : type === "income"
          ? "finance/income"
          : "finance/expense";
      return api.post(endpoint, data);
    },
    onSuccess: (data, variables) => {
      toast.success(`${variables.type} added successfully`);
      queryClient.invalidateQueries(["transactions"]);
      queryClient.invalidateQueries(["financeStats"]);
      setShowAddModal(false);
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message || "Failed to add transaction"
      );
    },
  });

  const editTransactionMutation = useMutation({
    mutationFn: async ({ type, transactionId, data }) => {
      const endpoint =
        type === "payment"
          ? `finance/payment/${transactionId}`
          : type === "income"
          ? `finance/income/${transactionId}`
          : `finance/expense/${transactionId}`;
      return api.put(endpoint, data);
    },
    onSuccess: (data, variables) => {
      toast.success(`${variables.type} updated successfully`);
      queryClient.invalidateQueries(["transactions"]);
      queryClient.invalidateQueries(["financeStats"]);
      setShowEditModal(false);
      setEditingTransaction(null);
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message || "Failed to update transaction"
      );
    },
  });

  const deleteTransactionMutation = useMutation({
    mutationFn: async (transactionId) => {
      return api.delete(`finance/transaction/${transactionId}`);
    },
    onSuccess: () => {
      toast.success("Transaction deleted successfully");
      queryClient.invalidateQueries(["transactions"]);
      queryClient.invalidateQueries(["financeStats"]);
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message || "Failed to delete transaction"
      );
    },
  });

  // Helper functions
  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: {
        icon: Clock,
        color: "bg-yellow-100 text-yellow-800 border-yellow-200",
        text: "অপেক্ষমাণ",
      },
      approved: {
        icon: CheckCircle,
        color: "bg-green-100 text-green-800 border-green-200",
        text: "অনুমোদিত",
      },
      rejected: {
        icon: XCircle,
        color: "bg-red-100 text-red-800 border-red-200",
        text: "প্রত্যাখ্যাত",
      },
    };

    const config = statusConfig[status] || statusConfig.pending;
    const IconComponent = config.icon;

    return (
      <span
        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${config.color}`}
      >
        <IconComponent size={12} className="mr-1" />
        {config.text}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    const typeConfig = {
      payment: {
        icon: CreditCard,
        color: "bg-blue-100 text-blue-800",
        text: "পেমেন্ট",
      },
      income: {
        icon: TrendingUp,
        color: "bg-green-100 text-green-800",
        text: "আয়",
      },
      expense: {
        icon: TrendingDown,
        color: "bg-red-100 text-red-800",
        text: "খরচ",
      },
    };

    const config = typeConfig[type] || typeConfig.payment;
    const IconComponent = config.icon;

    return (
      <span
        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}
      >
        <IconComponent size={12} className="mr-1" />
        {config.text}
      </span>
    );
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat("bn-BD", {
      style: "currency",
      currency: "BDT",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const handleEditTransaction = (transaction) => {
    setEditingTransaction(transaction);
    setModalType(transaction.type);
    setShowEditModal(true);
  };

  const handleDeleteTransaction = async (transaction) => {
    const confirmDelete = window.confirm(
      `আপনি কি নিশ্চিত যে আপনি "${transaction.title}" লেনদেনটি মুছে ফেলতে চান?`
    );
    
    if (confirmDelete) {
      deleteTransactionMutation.mutate(transaction._id);
    }
  };

  const StatsCards = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">মোট আয় (আয় + পেমেন্ট)</p>
            <p className="text-2xl font-bold text-green-600">
              {formatAmount(stats.currentMonth?.income || 0)}
            </p>
            {stats.currentMonth?.rawIncome && (
              <p className="text-xs text-gray-500">
                আয়: {formatAmount(stats.currentMonth.rawIncome)} + পেমেন্ট: {formatAmount(stats.currentMonth.payments)}
              </p>
            )}
            {stats.comparison?.incomeChange !== undefined && (
              <div
                className={`flex items-center text-sm ${
                  stats.comparison.incomeChange >= 0
                    ? "text-green-600"
                    : "text-red-600"
                }`}
              >
                {stats.comparison.incomeChange >= 0 ? (
                  <ArrowUpRight size={16} className="mr-1" />
                ) : (
                  <ArrowDownRight size={16} className="mr-1" />
                )}
                {Math.abs(stats.comparison.incomeChange).toFixed(1)}%
              </div>
            )}
          </div>
          <div className="p-3 bg-green-100 rounded-full">
            <TrendingUp className="w-6 h-6 text-green-600" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">এই মাসের খরচ</p>
            <p className="text-2xl font-bold text-red-600">
              {formatAmount(stats.currentMonth?.expense || 0)}
            </p>
            {stats.comparison?.expenseChange !== undefined && (
              <div
                className={`flex items-center text-sm ${
                  stats.comparison.expenseChange >= 0
                    ? "text-red-600"
                    : "text-green-600"
                }`}
              >
                {stats.comparison.expenseChange >= 0 ? (
                  <ArrowUpRight size={16} className="mr-1" />
                ) : (
                  <ArrowDownRight size={16} className="mr-1" />
                )}
                {Math.abs(stats.comparison.expenseChange).toFixed(1)}%
              </div>
            )}
          </div>
          <div className="p-3 bg-red-100 rounded-full">
            <TrendingDown className="w-6 h-6 text-red-600" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">সদস্য পেমেন্ট</p>
            <p className="text-2xl font-bold text-blue-600">
              {formatAmount(stats.currentMonth?.payments || 0)}
            </p>
            {stats.comparison?.paymentsChange !== undefined && (
              <div
                className={`flex items-center text-sm ${
                  stats.comparison.paymentsChange >= 0
                    ? "text-green-600"
                    : "text-red-600"
                }`}
              >
                {stats.comparison.paymentsChange >= 0 ? (
                  <ArrowUpRight size={16} className="mr-1" />
                ) : (
                  <ArrowDownRight size={16} className="mr-1" />
                )}
                {Math.abs(stats.comparison.paymentsChange).toFixed(1)}%
              </div>
            )}
          </div>
          <div className="p-3 bg-blue-100 rounded-full">
            <CreditCard className="w-6 h-6 text-blue-600" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">নেট ব্যালেন্স</p>
            <p
              className={`text-2xl font-bold ${
                (stats.currentMonth?.netBalance || 0) >= 0
                  ? "text-green-600"
                  : "text-red-600"
              }`}
            >
              {formatAmount(stats.currentMonth?.netBalance || 0)}
            </p>
            <div className="flex items-center text-sm text-gray-600">
              <AlertTriangle size={16} className="mr-1" />
              {stats.pendingPayments || 0} অপেক্ষমাণ
            </div>
          </div>
          <div className="p-3 bg-gray-100 rounded-full">
            <Wallet className="w-6 h-6 text-gray-600" />
          </div>
        </div>
      </div>
    </div>
  );

  const FiltersSection = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
        {/* Search */}
        <div className="md:col-span-2 relative">
          <Search
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            size={20}
          />
          <input
            type="text"
            placeholder="শিরোনাম, বিবরণ, রেফারেন্স..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>

        {/* Type Filter */}
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
        >
          <option value="all">সকল ধরন</option>
          <option value="payment">পেমেন্ট</option>
          <option value="income">আয়</option>
          <option value="expense">খরচ</option>
        </select>

        {/* Status Filter */}
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
        >
          <option value="all">সকল স্ট্যাটাস</option>
          <option value="pending">অপেক্ষমাণ</option>
          <option value="approved">অনুমোদিত</option>
          <option value="rejected">প্রত্যাখ্যাত</option>
        </select>

        {/* Month Filter */}
        <select
          value={monthFilter}
          onChange={(e) => setMonthFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
        >
          <option value="">সকল মাস</option>
          {Array.from({ length: 12 }, (_, i) => {
            const month = (i + 1).toString().padStart(2, "0");
            const monthName = new Date(2024, i).toLocaleDateString("bn-BD", {
              month: "long",
            });
            return (
              <option key={month} value={month}>
                {monthName}
              </option>
            );
          })}
        </select>

        {/* Year Filter */}
        <select
          value={yearFilter}
          onChange={(e) => setYearFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
        >
          {Array.from({ length: 5 }, (_, i) => {
            const year = (new Date().getFullYear() - i).toString();
            return (
              <option key={year} value={year}>
                {year}
              </option>
            );
          })}
        </select>
      </div>
    </div>
  );

  const TransactionTable = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left py-4 px-6 font-semibold text-gray-900">
                লেনদেন
              </th>
              <th className="text-left py-4 px-6 font-semibold text-gray-900">
                ধরন
              </th>
              <th className="text-left py-4 px-6 font-semibold text-gray-900">
                পরিমাণ
              </th>
              <th className="text-left py-4 px-6 font-semibold text-gray-900">
                তারিখ
              </th>
              <th className="text-left py-4 px-6 font-semibold text-gray-900">
                স্ট্যাটাস
              </th>
              <th className="text-left py-4 px-6 font-semibold text-gray-900">
                অ্যাকশন
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {transactions.map((transaction) => (
              <tr
                key={transaction._id}
                className="hover:bg-gray-50 transition-colors duration-150"
              >
                <td className="py-4 px-6">
                  <div className="flex items-center space-x-3">
                    {transaction.memberId && (
                      <img
                        src={
                          transaction.memberId.imageUrl
                            ? `${baseUrl}${transaction.memberId.imageUrl}`
                            : "/default-avatar.png"
                        }
                        alt={transaction.memberId.name}
                        className="w-8 h-8 rounded-full object-cover border border-gray-200"
                        onError={(e) => {
                          e.target.src = "/default-avatar.png";
                        }}
                      />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {transaction.title}
                      </p>
                      <p className="text-sm text-gray-600 truncate">
                        {transaction.memberId
                          ? `${transaction.memberId.name} (${transaction.memberId.membershipId})`
                          : transaction.source ||
                            transaction.vendor ||
                            "সিস্টেম"}
                      </p>
                      {transaction.referenceNumber && (
                        <p className="text-xs text-gray-500 font-mono">
                          {transaction.referenceNumber}
                        </p>
                      )}
                    </div>
                  </div>
                </td>

                <td className="py-4 px-6">
                  {getTypeBadge(transaction.type)}
                  <div className="text-xs text-gray-500 mt-1">
                    {transaction.category}
                  </div>
                </td>

                <td className="py-4 px-6">
                  <div
                    className={`text-sm font-semibold ${
                      transaction.type === "expense"
                        ? "text-red-600"
                        : "text-green-600"
                    }`}
                  >
                    {transaction.type === "expense" ? "-" : "+"}
                    {formatAmount(transaction.amount)}
                  </div>
                  {transaction.paymentMethod && (
                    <div className="text-xs text-gray-500 capitalize">
                      {transaction.paymentMethod}
                    </div>
                  )}
                </td>

                <td className="py-4 px-6">
                  <div className="text-sm text-gray-900">
                    {new Date(transaction.transactionDate).toLocaleDateString(
                      "bn-BD"
                    )}
                  </div>
                  {transaction.paymentMonth && (
                    <div className="text-xs text-gray-500">
                      মাস: {transaction.paymentMonth}
                    </div>
                  )}
                </td>

                <td className="py-4 px-6">
                  {getStatusBadge(transaction.status)}
                  {transaction.processedBy && (
                    <div className="text-xs text-gray-500 mt-1">
                      দ্বারা: {transaction.processedBy.name}
                    </div>
                  )}
                </td>

                <td className="py-4 px-6">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setSelectedTransaction(transaction)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-150"
                      title="বিস্তারিত দেখুন"
                    >
                      <Eye size={16} />
                    </button>

                    <button
                      onClick={() => handleEditTransaction(transaction)}
                      className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors duration-150"
                      title="সম্পাদনা করুন"
                    >
                      <Edit size={16} />
                    </button>

                    <button
                      onClick={() => handleDeleteTransaction(transaction)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-150"
                      title="মুছে ফেলুন"
                    >
                      <Trash2 size={16} />
                    </button>

                    {transaction.type === "payment" &&
                      transaction.status === "pending" && (
                        <>
                          <button
                            onClick={() =>
                              updateStatusMutation.mutate({
                                paymentId: transaction._id,
                                status: "approved",
                                adminNote: "Approved by admin",
                              })
                            }
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors duration-150"
                            title="অনুমোদন করুন"
                          >
                            <CheckCircle size={16} />
                          </button>

                          <button
                            onClick={() =>
                              updateStatusMutation.mutate({
                                paymentId: transaction._id,
                                status: "rejected",
                                adminNote: "Rejected by admin",
                              })
                            }
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-150"
                            title="প্রত্যাখ্যান করুন"
                          >
                            <XCircle size={16} />
                          </button>
                        </>
                      )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {transactions.length === 0 && (
        <div className="text-center py-12">
          <FileText size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            কোন লেনদেন পাওয়া যায়নি
          </h3>
          <p className="text-gray-600">
            বর্তমানে কোন আর্থিক লেনদেন নেই বা ফিল্টার অনুযায়ী কিছু পাওয়া
            যায়নি।
          </p>
        </div>
      )}
    </div>
  );

  if (statsLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              আর্থিক ব্যবস্থাপনা
            </h1>
            <p className="text-gray-600">
              সংগঠনের সম্পূর্ণ আর্থিক লেনদেন ও রিপোর্ট পরিচালনা
            </p>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => {
                  setModalType("payment");
                  setShowAddModal(true);
                }}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors duration-200"
              >
                <CreditCard size={16} className="mr-2 inline" />
                পেমেন্ট
              </button>
              <button
                onClick={() => {
                  setModalType("income");
                  setShowAddModal(true);
                }}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors duration-200 ml-2"
              >
                <TrendingUp size={16} className="mr-2 inline" />
                আয়
              </button>
              <button
                onClick={() => {
                  setModalType("expense");
                  setShowAddModal(true);
                }}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors duration-200 ml-2"
              >
                <TrendingDown size={16} className="mr-2 inline" />
                খরচ
              </button>
            </div>

            <button
              onClick={() => refetchTransactions()}
              className="px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium transition-colors duration-200"
            >
              <RefreshCw size={16} className="mr-2 inline" />
              রিফ্রেশ
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <StatsCards />

      {/* Filters */}
      <FiltersSection />

      {/* Transaction Table */}
      {transactionsLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        </div>
      ) : (
        <TransactionTable />
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            দেখানো হচ্ছে {(pagination.current - 1) * 20 + 1} থেকে{" "}
            {Math.min(pagination.current * 20, pagination.total)} পর্যন্ত, মোট{" "}
            {pagination.total}
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={!pagination.hasPrev}
              className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              পূর্ববর্তী
            </button>

            {Array.from({ length: Math.min(pagination.pages, 5) }, (_, i) => {
              const page = i + Math.max(1, pagination.current - 2);
              return (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-2 text-sm font-medium rounded-lg ${
                    page === pagination.current
                      ? "bg-primary text-white"
                      : "text-gray-600 bg-white border border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {page}
                </button>
              );
            })}

            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, pagination.pages))
              }
              disabled={!pagination.hasNext}
              className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              পরবর্তী
            </button>
          </div>
        </div>
      )}

      {/* Add Transaction Modals */}
      {showAddModal && (
        <TransactionModal
          type={modalType}
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSubmit={(data) => {
            addTransactionMutation.mutate({ type: modalType, data });
          }}
          isLoading={addTransactionMutation.isLoading}
        />
      )}

      {/* Edit Transaction Modal */}
      {showEditModal && editingTransaction && (
        <TransactionModal
          type={modalType}
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingTransaction(null);
          }}
          onSubmit={(data) => {
            editTransactionMutation.mutate({ 
              type: modalType, 
              transactionId: editingTransaction._id,
              data 
            });
          }}
          isLoading={editTransactionMutation.isLoading}
          editData={editingTransaction}
          isEdit={true}
        />
      )}

      {/* Transaction Details Modal */}
      {selectedTransaction && (
        <TransactionDetailsModal
          transaction={selectedTransaction}
          isOpen={!!selectedTransaction}
          onClose={() => setSelectedTransaction(null)}
        />
      )}
    </div>
  );
};

// Transaction Modal Component
const TransactionModal = ({ type, isOpen, onClose, onSubmit, isLoading, editData = null, isEdit = false }) => {
  const [formData, setFormData] = useState({
    title: "",
    amount: "",
    category: "",
    description: "",
    source: "",
    vendor: "",
    transactionDate: new Date().toISOString().split("T")[0],
    referenceNumber: "",
    // Payment specific fields
    memberId: "",
    month: "", // Backend expects 'month', not 'paymentMonth'
    paymentMethod: "cash",
  });
  
  const [users, setUsers] = useState([]);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  
  // Fetch users when modal opens for payment type
  useEffect(() => {
    if (type === 'payment' && isOpen) {
      const fetchUsers = async () => {
        try {
          const response = await api.get('/user');
          // Filter for members and active users
          const members = response.data.data.users.filter(user => 
            (user.role === 'member' || user.role === 'admin') && user.status === 'active'
          ) || [];
          setUsers(members);
        } catch (error) {
          console.error('Failed to fetch users:', error);
          toast.error('ব্যবহারকারীদের তালিকা লোড করতে ব্যর্থ');
        }
      };
      fetchUsers();
    }
  }, [type, isOpen]);
  
  // Filter users based on search term
  const filteredUsers = users.filter(user => {
    const searchLower = userSearchTerm.toLowerCase();
    const userName = user.name || '';
    const userMembershipId = user.membershipId || '';
    const userMobile = user.mobile || '';
    
    return userName.toLowerCase().includes(searchLower) ||
           userMembershipId.toLowerCase().includes(searchLower) ||
           userMobile.includes(userSearchTerm);
  });
  
  // Handle user selection
  const handleUserSelect = (user) => {
    handleInputChange("memberId", user._id);
    const userName = user.name || 'নাম নেই';
    const userMembershipId = user.membershipId || 'ID নেই';
    setUserSearchTerm(`${userName} - ${userMembershipId}`);
    setShowUserDropdown(false);
  };
  
  // Initialize form data when modal opens (for edit mode)
  useEffect(() => {
    if (isOpen && isEdit && editData) {
      const transactionDate = editData.transactionDate 
        ? new Date(editData.transactionDate).toISOString().split("T")[0]
        : new Date().toISOString().split("T")[0];
      
      setFormData({
        title: editData.title || "",
        amount: editData.amount || "",
        category: editData.category || "",
        description: editData.description || "",
        source: editData.source || "",
        vendor: editData.vendor || "",
        transactionDate: transactionDate,
        referenceNumber: editData.referenceNumber || "",
        memberId: editData.memberId?._id || "",
        month: editData.paymentMonth || "",
        paymentMethod: editData.paymentMethod || "cash",
      });
      
      if (editData.memberId) {
        const memberName = editData.memberId.name || 'নাম নেই';
        const membershipId = editData.memberId.membershipId || 'ID নেই';
        setUserSearchTerm(`${memberName} - ${membershipId}`);
      }
    } else if (isOpen && !isEdit) {
      // Reset form for new transaction
      setFormData({
        title: "",
        amount: "",
        category: "",
        description: "",
        source: "",
        vendor: "",
        transactionDate: new Date().toISOString().split("T")[0],
        referenceNumber: "",
        memberId: "",
        month: "",
        paymentMethod: "cash",
      });
      setUserSearchTerm("");
    }
    
    if (!isOpen) {
      setShowUserDropdown(false);
    }
  }, [isOpen, isEdit, editData]);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserDropdown && !event.target.closest('.relative')) {
        setShowUserDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserDropdown]);

  const categories = {
    payment: ["monthly_fee", "event_fee", "registration_fee", "other"],
    income: [
      "donation",
      "membership_fee",
      "event_income",
      "fundraising",
      "government_grant",
      "other",
    ],
    expense: [
      "office_rent",
      "utility_bills",
      "event_cost",
      "transportation",
      "office_supplies",
      "maintenance",
      "other",
    ],
  };

  const categoryLabels = {
    payment: {
      monthly_fee: "মাসিক ফি",
      event_fee: "ইভেন্ট ফি",
      registration_fee: "রেজিস্ট্রেশন ফি",
      other: "অন্যান্য",
    },
    income: {
      donation: "অনুদান",
      membership_fee: "সদস্যপদ ফি",
      event_income: "ইভেন্ট আয়",
      fundraising: "অনুষ্ঠান আয়",
      government_grant: "সরকারি অনুদান",
      other: "অন্যান্য",
    },
    expense: {
      office_rent: "অফিস ভাড়া",
      utility_bills: "ইউটিলিটি বিল",
      event_cost: "ইভেন্ট খরচ",
      transportation: "পরিবহন",
      office_supplies: "অফিস সরঞ্জাম",
      maintenance: "রক্ষণাবেক্ষণ",
      other: "অন্যান্য",
    },
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {isEdit
              ? (type === "payment"
                  ? "পেমেন্ট সম্পাদনা করুন"
                  : type === "income"
                  ? "আয় সম্পাদনা করুন"
                  : "খরচ সম্পাদনা করুন")
              : (type === "payment"
                  ? "নতুন পেমেন্ট যোগ করুন"
                  : type === "income"
                  ? "নতুন আয় যোগ করুন"
                  : "নতুন খরচ যোগ করুন")}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              শিরোনাম *
            </label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => handleInputChange("title", e.target.value)}
              placeholder="লেনদেনের শিরোনাম লিখুন"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              বিভাগ *
            </label>
            <select
              required
              value={formData.category}
              onChange={(e) => handleInputChange("category", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">বিভাগ নির্বাচন করুন</option>
              {categories[type].map((cat) => (
                <option key={cat} value={cat}>
                  {categoryLabels[type][cat]}
                </option>
              ))}
            </select>
          </div>

          {/* Payment specific fields */}
          {type === "payment" && (
            <>
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  সদস্য নির্বাচন করুন *
                </label>
                <input
                  type="text"
                  required
                  value={userSearchTerm}
                  onChange={(e) => {
                    setUserSearchTerm(e.target.value);
                    setShowUserDropdown(true);
                    if (!e.target.value) {
                      handleInputChange("memberId", "");
                    }
                  }}
                  onFocus={() => setShowUserDropdown(true)}
                  placeholder="নাম, সদস্য নং বা মোবাইল দিয়ে খুঁজুন..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                
                {showUserDropdown && filteredUsers.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredUsers.slice(0, 10).map((user) => (
                      <div
                        key={user._id}
                        onClick={() => handleUserSelect(user)}
                        className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                      >
                        <div className="font-medium text-gray-900">{user.name || 'নাম নেই'}</div>
                        <div className="text-sm text-gray-600">
                          {user.membershipId || 'ID নেই'} • {user.mobile || 'মোবাইল নেই'}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {showUserDropdown && userSearchTerm && filteredUsers.length === 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-3">
                    <div className="text-gray-500 text-sm">কোন সদস্য পাওয়া যায়নি</div>
                  </div>
                )}
              </div>

              {formData.category === "monthly_fee" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    পেমেন্টের মাস *
                  </label>
                  <input
                    type="month"
                    required
                    value={formData.month ? `${formData.month.split('-')[1]}-${formData.month.split('-')[0]}` : ""}
                    onChange={(e) => {
                      const [year, month] = e.target.value.split('-');
                      const formattedMonth = `${month}-${year}`;
                      handleInputChange("month", formattedMonth);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  পেমেন্ট পদ্ধতি *
                </label>
                <select
                  required
                  value={formData.paymentMethod}
                  onChange={(e) => handleInputChange("paymentMethod", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="cash">নগদ</option>
                  <option value="bkash">বিকাশ</option>
                  <option value="nagad">নগদ (মোবাইল ব্যাংকিং)</option>
                  <option value="bank">ব্যাংক ট্রান্সফার</option>
                </select>
              </div>
            </>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              পরিমাণ (টাকা) *
            </label>
            <input
              type="number"
              required
              min="1"
              value={formData.amount}
              onChange={(e) => handleInputChange("amount", e.target.value)}
              placeholder="0"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          {type === "income" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                আয়ের উৎস
              </label>
              <input
                type="text"
                value={formData.source}
                onChange={(e) => handleInputChange("source", e.target.value)}
                placeholder="যেমন: দানকারীর নাম, প্রতিষ্ঠানের নাম"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          )}

          {type === "expense" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                বিক্রেতা/সরবরাহকারী
              </label>
              <input
                type="text"
                value={formData.vendor}
                onChange={(e) => handleInputChange("vendor", e.target.value)}
                placeholder="যেমন: দোকানের নাম, প্রতিষ্ঠানের নাম"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              বিবরণ
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              placeholder="লেনদেন সম্পর্কে অতিরিক্ত তথ্য..."
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              তারিখ *
            </label>
            <input
              type="date"
              required
              value={formData.transactionDate}
              onChange={(e) =>
                handleInputChange("transactionDate", e.target.value)
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              রেফারেন্স নম্বর
            </label>
            <input
              type="text"
              value={formData.referenceNumber}
              onChange={(e) =>
                handleInputChange("referenceNumber", e.target.value)
              }
              placeholder="যেমন: চেক নম্বর, রশিদ নম্বর"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors duration-200"
            >
              বাতিল
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className={`px-4 py-2 text-white rounded-lg disabled:opacity-50 transition-colors duration-200 flex items-center ${
                type === "payment"
                  ? "bg-blue-600 hover:bg-blue-700"
                  : type === "income"
                  ? "bg-green-600 hover:bg-green-700"
                  : "bg-red-600 hover:bg-red-700"
              }`}
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                  {isEdit ? "আপডেট হচ্ছে..." : "যোগ করা হচ্ছে..."}
                </>
              ) : (
                <>
                  {isEdit ? <Edit size={16} className="mr-2" /> : <Plus size={16} className="mr-2" />}
                  {isEdit ? "আপডেট করুন" : "যোগ করুন"}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Transaction Details Modal Component
const TransactionDetailsModal = ({ transaction, isOpen, onClose }) => {
  if (!isOpen || !transaction) return null;

  const formatAmount = (amount) => {
    return new Intl.NumberFormat("bn-BD", {
      style: "currency",
      currency: "BDT",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            লেনদেনের বিস্তারিত
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <span className="text-sm font-medium text-gray-600">শিরোনাম</span>
            <span className="text-sm font-semibold text-gray-900">
              {transaction.title}
            </span>
          </div>

          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <span className="text-sm font-medium text-gray-600">ধরন</span>
            <span
              className={`text-sm font-semibold px-3 py-1 rounded-full ${
                transaction.type === "income"
                  ? "bg-green-100 text-green-800"
                  : transaction.type === "expense"
                  ? "bg-red-100 text-red-800"
                  : "bg-blue-100 text-blue-800"
              }`}
            >
              {transaction.type === "income"
                ? "আয়"
                : transaction.type === "expense"
                ? "খরচ"
                : "পেমেন্ট"}
            </span>
          </div>

          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <span className="text-sm font-medium text-gray-600">পরিমাণ</span>
            <span
              className={`text-lg font-bold ${
                transaction.type === "expense"
                  ? "text-red-600"
                  : "text-green-600"
              }`}
            >
              {transaction.type === "expense" ? "-" : "+"}
              {formatAmount(transaction.amount)}
            </span>
          </div>

          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <span className="text-sm font-medium text-gray-600">বিভাগ</span>
            <span className="text-sm font-semibold text-gray-900">
              {transaction.category}
            </span>
          </div>

          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <span className="text-sm font-medium text-gray-600">তারিখ</span>
            <span className="text-sm font-semibold text-gray-900">
              {new Date(transaction.transactionDate).toLocaleDateString(
                "bn-BD"
              )}
            </span>
          </div>

          {transaction.source && (
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <span className="text-sm font-medium text-gray-600">
                আয়ের উৎস
              </span>
              <span className="text-sm font-semibold text-gray-900">
                {transaction.source}
              </span>
            </div>
          )}

          {transaction.vendor && (
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <span className="text-sm font-medium text-gray-600">
                বিক্রেতা
              </span>
              <span className="text-sm font-semibold text-gray-900">
                {transaction.vendor}
              </span>
            </div>
          )}

          {transaction.referenceNumber && (
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <span className="text-sm font-medium text-gray-600">
                রেফারেন্স
              </span>
              <span className="text-sm font-semibold text-gray-900 font-mono">
                {transaction.referenceNumber}
              </span>
            </div>
          )}

          {transaction.description && (
            <div className="p-4 bg-gray-50 rounded-lg">
              <span className="text-sm font-medium text-gray-600 block mb-2">
                বিবরণ
              </span>
              <p className="text-sm text-gray-900">{transaction.description}</p>
            </div>
          )}

          {transaction.createdBy && (
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <span className="text-sm font-medium text-gray-600">যোগকারী</span>
              <span className="text-sm font-semibold text-gray-900">
                {transaction.createdBy.name}
              </span>
            </div>
          )}

          {transaction.processedBy && (
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <span className="text-sm font-medium text-gray-600">
                অনুমোদনকারী
              </span>
              <span className="text-sm font-semibold text-gray-900">
                {transaction.processedBy.name}
              </span>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors duration-200"
          >
            বন্ধ করুন
          </button>
        </div>
      </div>
    </div>
  );
};

export default FinanceManagement;
